// Shared HTTP helper for all API service modules.
// Centralizes the base URL, auth header, JSON handling and error checking.
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Make a request against the API.
 *
 * @param {string} path - Path starting with '/', appended to the base URL.
 * @param {object} [options]
 * @param {string} [options.method='GET']
 * @param {*} [options.body] - When set, sent as JSON (adds Content-Type).
 * @param {string} [options.userId] - When set, sent as the 'x-user-id' header.
 * @param {object} [options.headers] - Extra headers to merge in.
 * @returns {Promise<*>} Parsed JSON, or null for empty/No-Content responses.
 */
export const apiRequest = async (
	path,
	{ method = 'GET', body, userId, headers } = {}
) => {
	const finalHeaders = { ...headers };
	if (userId != null) finalHeaders['x-user-id'] = userId;
	if (body !== undefined) finalHeaders['Content-Type'] = 'application/json';

	const response = await fetch(`${BASE_URL}${path}`, {
		method,
		headers: finalHeaders,
		...(body !== undefined ? { body: JSON.stringify(body) } : {}),
	});

	if (!response.ok) {
		const error = new Error(
			`Request failed (${response.status}): ${method} ${path}`
		);
		// Callers that need to tell one failure from another (e.g. a 422 on an
		// out-of-range report) read this instead of parsing the message.
		error.status = response.status;
		throw error;
	}

	// Handle 204 No Content and any other empty-body responses gracefully
	if (response.status === 204) return null;
	const text = await response.text();
	return text ? JSON.parse(text) : null;
};
